import React, { useState } from 'react';
import { Percent, Users, Clock, Save, AlertCircle, RotateCcw } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/Label';
import { Toast } from './ui/Toast';
import useSchoolParams from '../hooks/useSchoolParams';
import EnTeteParametrage from './parametrage/EnTeteParametrage';
import EnTeteSection from './parametrage/EnTeteSection';
import CarteReglages from './parametrage/CarteReglages';
import ChargementSection from './parametrage/ChargementSection';

/**
 * Paramétrage de la scolarité : ce que l'école facture aux familles.
 *
 * ----------------------------------------------------------------------------
 * Pourquoi une page à part
 * ----------------------------------------------------------------------------
 * Ces deux réglages vivaient dans « Identité de l'établissement », entre le
 * logo et l'adresse postale. Ils n'y avaient pas leur place : ils bornent de
 * l'argent, et on ne va pas chercher un plafond de réduction dans la fiche
 * d'identité de l'école.
 *
 * Ils ne pouvaient pas non plus rejoindre « Gestion comptable » ni « Contrôle
 * des salaires » : ces deux pages sont derrière le verrou `module.paie`, et une
 * école qui n'a pas acheté la paie aurait perdu l'accès à ses propres
 * réductions d'élèves. La paie, c'est ce que l'école verse ; la scolarité,
 * c'est ce qu'elle reçoit.
 *
 * Les données restent celles de `useSchoolParams` — même table, même
 * enregistrement. Seule la porte d'entrée change.
 */

const ACCENT = '#319190';
const ACCENT_SOMBRE = '#2a7a79';

const ParametresScolarite = ({ onBackToDashboard }) => {
  const {
    schoolInfo,
    handleInputChange,
    updateSchoolParams,
    loadSchoolParams,
    loading,
  } = useSchoolParams();

  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  const avertir = (message, type = 'error') => setToast({ show: true, message, type });

  // Régime B de la norme : on modifie en local, puis on valide. Les trois
  // valeurs se tiennent — un plafond, un seuil, un délai — et les enregistrer
  // une par une n'aurait pas de sens.
  const modifier = (champ, valeur) => {
    handleInputChange(champ, valeur);
    setHasChanges(true);
  };

  // Le retour en arriere du regime B : on relit ce que le serveur a, plutot
  // que de memoriser une copie locale qui pourrait diverger.
  const reinitialiser = async () => {
    await loadSchoolParams();
    setHasChanges(false);
  };

  const enregistrer = async () => {
    setSaving(true);
    try {
      const result = await updateSchoolParams(schoolInfo);
      if (result.success) {
        setToast({ show: true, message: 'Réglages enregistrés', type: 'success' });
        setHasChanges(false);
      } else {
        avertir(result.error || "Erreur lors de l'enregistrement");
      }
    } catch (error) {
      avertir('Erreur de connexion au serveur');
    } finally {
      setSaving(false);
    }
  };

  const enTete = (
    <EnTeteParametrage
      onRetour={onBackToDashboard}
      titre="Paramétrage de la scolarité"
      sousTitre="Plafond des réductions et règles des familles nombreuses"
    />
  );

  if (loading) {
    return (
      <div className="w-full">
        {enTete}
        <div className="bg-white rounded-xl shadow-[0_1px_4px_rgba(0,0,0,0.06)] border border-gray-200/80 p-6">
          <ChargementSection accent={ACCENT} libelle="Chargement des réglages…" />
        </div>
      </div>
    );
  }

  const plafond = schoolInfo.plafond_reduction_pourcentage ?? 100;
  const minEnfants = schoolInfo.famille_nombreuse_min_enfants ?? 2;
  const delai = schoolInfo.famille_nombreuse_delai_regularisation_jours ?? 7;

  return (
    <div className="w-full">
      {toast.show && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ ...toast, show: false })}
        />
      )}

      {enTete}

      <div className="bg-white rounded-xl shadow-[0_1px_4px_rgba(0,0,0,0.06)] border border-gray-200/80 p-6">
        <div className="space-y-5">
          <EnTeteSection
            icone={Percent}
            titre="Ce que l'école accorde aux familles"
            sousTitre="Ces règles bornent les réductions saisies sur chaque élève"
            accent={ACCENT}
            accentSombre={ACCENT_SOMBRE}
            actions={
              <>
                {/* Format d'en-tete de section, PARAMETRAGE-MODULES.md §5.
                    Ces boutons portaient `h-9 min-w-[168px]`, la constante du
                    pied de modal : deux fois trop large pour cet emplacement. */}
                <button
                  type="button"
                  onClick={reinitialiser}
                  disabled={saving || !hasChanges}
                  className="flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3.5 py-2 text-xs font-semibold text-gray-500 transition-all hover:border-gray-300 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <RotateCcw size={13} /> Réinitialiser
                </button>
                <button
                  type="button"
                  onClick={enregistrer}
                  disabled={saving || !hasChanges}
                  className={`flex items-center gap-1.5 rounded-lg border border-transparent px-4 py-2 text-xs font-semibold text-white shadow-sm transition-all ${
                    hasChanges ? 'bg-[#246990] hover:shadow-md' : 'cursor-not-allowed bg-gray-300 shadow-none'
                  }`}
                >
                  {saving
                    ? <div className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                    : <Save size={13} />}
                  Enregistrer
                </button>
              </>
            }
          />

          {/* ------------------------------------------------------------ */}
          <CarteReglages titre="Plafond des réductions" accent={ACCENT}>
            <div className="flex flex-wrap items-start justify-between gap-6">
              <div className="max-w-xl">
                <p className="text-sm font-semibold text-gray-800">
                  Réduction maximale sur les frais d'un élève
                </p>
                <p className="mt-1.5 text-[11px] leading-relaxed text-gray-400">
                  Au-delà de ce pourcentage, la saisie d'une réduction est refusée.
                  100 % autorise une gratuité complète.
                </p>
              </div>
              <div className="relative w-32 flex-shrink-0">
                <Input
                  type="number"
                  min="0"
                  max="100"
                  value={plafond}
                  onChange={(e) =>
                    modifier(
                      'plafond_reduction_pourcentage',
                      Math.min(100, Math.max(0, Number(e.target.value) || 0))
                    )
                  }
                  className="h-9 rounded-lg border-gray-200 pr-9 text-center focus:border-[#246990] focus:ring-2 focus:ring-[#246990]/15"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">
                  %
                </span>
              </div>
            </div>
          </CarteReglages>

          {/* ------------------------------------------------------------ */}
          <CarteReglages titre="Familles nombreuses" accent={ACCENT} dense>
            <div className="flex flex-wrap items-start justify-between gap-6 px-5 py-4">
              <div className="max-w-xl">
                <div className="flex items-center gap-2">
                  <Users size={15} className="text-gray-400" />
                  <p className="text-sm font-semibold text-gray-800">Minimum d'enfants</p>
                </div>
                <p className="mt-1.5 text-[11px] leading-relaxed text-gray-400">
                  Nombre d'enfants inscrits à partir duquel une famille ouvre droit à la
                  réduction famille nombreuse.
                </p>
              </div>
              <Input
                type="number"
                min="1"
                max="20"
                value={minEnfants}
                onChange={(e) =>
                  modifier(
                    'famille_nombreuse_min_enfants',
                    Math.min(20, Math.max(1, Number(e.target.value) || 2))
                  )
                }
                className="h-9 w-32 flex-shrink-0 rounded-lg border-gray-200 text-center focus:border-[#246990] focus:ring-2 focus:ring-[#246990]/15"
              />
            </div>

            <div className="flex flex-wrap items-start justify-between gap-6 px-5 py-4">
              <div className="max-w-xl">
                <div className="flex items-center gap-2">
                  <Clock size={15} className="text-gray-400" />
                  <p className="text-sm font-semibold text-gray-800">Délai avant archivage</p>
                </div>
                <p className="mt-1.5 text-[11px] leading-relaxed text-gray-400">
                  Si une famille repasse sous le minimum, elle reste visible « à
                  régulariser » pendant ce délai. À l'expiration elle est archivée
                  automatiquement — sans radier aucun élève.
                </p>
              </div>
              <div className="relative w-32 flex-shrink-0">
                <Input
                  type="number"
                  min="1"
                  max="365"
                  value={delai}
                  onChange={(e) =>
                    modifier(
                      'famille_nombreuse_delai_regularisation_jours',
                      Math.min(365, Math.max(1, Number(e.target.value) || 7))
                    )
                  }
                  className="h-9 rounded-lg border-gray-200 pr-12 text-center focus:border-[#246990] focus:ring-2 focus:ring-[#246990]/15"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">
                  jours
                </span>
              </div>
            </div>
          </CarteReglages>

          {/* Le rappel de la norme : la section est longue, le bouton du haut
              peut être sorti du champ. */}
          {hasChanges && (
            <div className="flex items-center gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4">
              <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-amber-100">
                <AlertCircle size={15} className="text-amber-600" />
              </div>
              <p className="text-xs font-semibold text-amber-700">
                Vous avez des modifications non enregistrées.
              </p>
              <Button
                onClick={enregistrer}
                disabled={saving}
                className="ml-auto h-9 min-w-[168px] justify-center gap-2 bg-[#246990] text-white hover:bg-[#1f5a7a]"
              >
                <Save className="h-4 w-4" />
                {saving ? 'Enregistrement…' : 'Enregistrer maintenant'}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ParametresScolarite;